import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Tooltip } from 'antd'
import { black } from '@edulastic/colors'
import {
  MfaCodeModalStyled,
  ModalBody,
  CodeDisplayRow,
  CodeDisplay,
  CopyCodeButton,
  StyledIconCopyContent,
  Description,
  ExpiryNote,
  FooterActions,
  OKButton,
} from './styled'

const GenerateMfaCodeModal = ({
  visible,
  code,
  expiresInMinutes,
  noActiveSession,
  userEmail,
  onClose,
  onAfterClose,
  t,
}) => {
  const isNoActiveSession = Boolean(noActiveSession)
  const hasCode = Boolean(code) && !isNoActiveSession
  const [copied, setCopied] = useState(false)

  const handleAfterClose = useCallback(() => {
    setCopied(false)
    onAfterClose?.()
  }, [onAfterClose])

  const handleCopyCode = useCallback(() => {
    if (!code) {
      return
    }
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    })
  }, [code])

  return (
    <MfaCodeModalStyled
      visible={visible}
      title={
        isNoActiveSession
          ? t('users.mfaCode.modalTitleNoSession')
          : t('users.mfaCode.modalTitle')
      }
      onCancel={onClose}
      afterClose={handleAfterClose}
      footer={null}
      maskClosable
      centered
      modalWidth="500px"
      modalMaxWidth="500px"
      padding="32px"
    >
      <ModalBody>
        <Description
          data-cy="adminMfaCodeModalDescription"
          $isError={!!isNoActiveSession}
        >
          {isNoActiveSession
            ? t('users.mfaCode.errors.noActiveSession')
            : t('users.mfaCode.modalDescription', {
                email: userEmail || '',
              })}
        </Description>
        {!isNoActiveSession ? (
          <>
            <CodeDisplayRow>
              <CodeDisplay data-cy="adminGeneratedMfaCode">
                {hasCode ? code : '------'}
              </CodeDisplay>
              {hasCode ? (
                <Tooltip
                  title={
                    copied
                      ? t('users.mfaCode.copied')
                      : t('users.mfaCode.copyCode')
                  }
                >
                  <CopyCodeButton
                    type="button"
                    onClick={handleCopyCode}
                    aria-label={t('users.mfaCode.copyCode')}
                    data-cy="adminGeneratedMfaCodeCopy"
                  >
                    <StyledIconCopyContent color={black} />
                  </CopyCodeButton>
                </Tooltip>
              ) : null}
            </CodeDisplayRow>
            <ExpiryNote>
              {t('users.mfaCode.expiresIn', {
                minutes: expiresInMinutes || 10,
              })}
            </ExpiryNote>
          </>
        ) : null}
        <FooterActions>
          {isNoActiveSession ? (
            <OKButton
              type="button"
              onClick={onClose}
              data-cy="mfaCodeModalOKButton"
            >
              {t('users.mfaCode.ok')}
            </OKButton>
          ) : null}
        </FooterActions>
      </ModalBody>
    </MfaCodeModalStyled>
  )
}

GenerateMfaCodeModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  code: PropTypes.string,
  expiresInMinutes: PropTypes.number,
  noActiveSession: PropTypes.bool,
  userEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
  t: PropTypes.func.isRequired,
}

GenerateMfaCodeModal.defaultProps = {
  code: '',
  expiresInMinutes: 10,
  noActiveSession: false,
  userEmail: '',
  onAfterClose: undefined,
}

export default GenerateMfaCodeModal
